import type { ButtonHTMLAttributes } from 'react';

import focus from '../primitives/focus.module.css';
import styles from './Fab.module.css';

export interface FabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** What it does, for a screen reader. The glyph is decorative. */
  readonly label: string;
  readonly onPress: () => void;
}

/**
 * The `+` on `LED-01` and `LED-05` — the way into `LED-02`, and the most-tapped control in the product.
 *
 * It sits above the nav bar rather than in it, which is the screen book's arrangement and the right one:
 * the four tabs are places you go, and this is a thing you do. Putting it in the bar would make "add an
 * entry" look like a fifth destination and cost it the size that makes it easy to hit with a thumb.
 *
 * **The glyph is `aria-hidden` and the button is named.** A screen reader announcing "plus" would be
 * reading the decoration; `label` is what it says instead, and there is no default for it — a floating
 * button with no name is the classic unlabelled control.
 */
export function Fab({ label, onPress, className, ...rest }: FabProps): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onPress}
      className={[styles.fab, focus.focusable, className ?? ''].filter(Boolean).join(' ')}
      {...rest}
    >
      <span aria-hidden="true">+</span>
    </button>
  );
}
